/* eslint-disable react/jsx-pascal-case */
/* eslint-disable no-underscore-dangle */
import React from "react"
import { connect } from "react-redux"
import { createStructuredSelector, createSelector } from "reselect"
import {
    selectIsLimitedToTeamsEnabled,
    selectIsTeamsLoading,
    selectShouldHideTeamPermissioningInForms,
    selectTeamsForComponent,
} from "shared/userdata/userdata-selectors"
import { change, resetSection } from "redux-form/immutable"
import { getUserdataField } from "shared/imports/userdataFunctions"

// constants
import {
    READ_JUST_ME,
} from "shared/imports/sharedConstants"

// determine if the user has any teams at all
// if the user has no teams, don't show this section as it is not applicable
const selectHasTeams = createSelector(
    selectTeamsForComponent,
    teams => Boolean(teams && teams.length)
)

/**
 * This function creates a TeamSelectSection form component
 * @name createConnectedTeamSelectSection
 * @function
 * @param {Object} args - An Object of arguments with the following structure
 * - actionCreators {Object} - An object of action creators, to be passed to teamSelectMapDispatchToProps
 * - isCreator {Boolean} - whether or not the user viewing this object in either creation/edit mode is the creator
 *      this prop, along with enableLimitingToCreator that is computed from the enableLimitedToTeamsOptions, determines
 *      whether to show the "Just Me" button
 *
 * - isLimitedToCreatorSelector {Function} - A reselect selector to provide a Boolean that represents whether this
 * item should be limited to Creator (called Just Me on the UI)
 *
 * - selector {Function} - A reselect selector to provide an array of team URI's via a teamUrisArray prop
 *
 * - selectPossibleTeamsForComponent {Function} - A reselect selector to provide an alternative Array of Team
 *  objects, in the case where the teams should NOT be pulled from the userdata slice of the store
 *
 * - shouldHideTeamPermissioningFormComponent - Some users don't have permission to change their permissioning settings.
 * We hide the component but still send the team permissioning information along that is configured by an admin
 *
 * - WrapperComponent {React.Component} - An optional wrapping component that will pass the connected
 *      team select to it as children. Example usage:
 *      WrapperComponent: ({ children }) => (<div className="overlay-title"> {children} </div>)
 *
 * - additionalShouldRenderFormSelector {Function} - an additional selector to apply with selectTeamsForComponent to determine if we should
 *  render the Limited to Teams Section
 *
 * Additional props are passed directly to the component. These can be either passed via this factory function,
 *  or provided in the render method where this function is to be rendered
 *
 * @returns {React.Component} - a connected React TeamSelectSection component
 *
 * Additional Notes if passing shouldParentManageFormStep, make sure that your component
 * respects shouldRenderFormSection, as this boolean hides the team permissioning components
 * if an organization has no teams
 */
const createConnectedTeamSelectSection = ({
    actionCreators,
    additionalShouldRenderFormSelector = () => true,
    isCreatorSelector = () => null,
    isLimitedToCreatorSelector = () => null,
    LimitedToTeamsSection,
    selector,
    shouldHideTeamPermissioningFormComponent,
    shouldHideTeamPermissioningFormComponentSelector,
    TeamSelectSection,
    WrapperComponent,
    ...additionalProps
}) => {
    class ConnectedWrappedTeamSelectSection extends React.Component {
        constructor(props) {
            super(props)
            // Provide dispatch to all actionCreators passed via the actionCreators object
            const mapDispatchToProps = {
                change,
                resetSection,
                ...actionCreators,
            }
            // provide an array of selected Team URI's via a selector to this component
            const teamSelectConnection = createStructuredSelector({
                ...selector && {
                    teamUrisArray: selector
                },
                // in some cases, we need to provide a different set of possible choices, such as in the UserForm
                // when creating a user for a different organization, and that organization will have different Teams
                possibleChoices: additionalProps.selectPossibleTeamsForComponent || selectTeamsForComponent,
            })

            // connect the component with action creators and the selector
            const _TeamSelectSection = connect(
                teamSelectConnection,
                mapDispatchToProps,
            )(TeamSelectSection)

            this._TeamSelectSection = _TeamSelectSection

            // provide a way to wrap the component in an additional component, useful for creating form components
            if (WrapperComponent) {

                // need this prop to be passed down as well
                const enableLimitingToCreator = Boolean((additionalProps.enableLimitedToTeamsOptions || []).filter(option => option.value === READ_JUST_ME))
                const selectCanLimitToCreator = createSelector(
                    isCreatorSelector,
                    isCreator => enableLimitingToCreator && isCreator
                )

                const selectShouldRenderFormSection = createSelector(
                    selectHasTeams,
                    additionalShouldRenderFormSelector,
                    selectCanLimitToCreator,
                    (hasTeams, additionalBoolean, canLimitToCreator) => Boolean((hasTeams || canLimitToCreator) && additionalBoolean)
                )


                // The wrapper component also needs to be aware of:
                // - teamUrisArray, for when in editing mode, when no teams are selected, we want to toggle the "Available to everyone"
                // - canLimitToCreator, to determine if we should toggle the Just Me selection
                const wrapperComponentConnection = createStructuredSelector({
                    canLimitToCreator: selectCanLimitToCreator,
                    // in some cases, we need to provide a different set of possible choices, such as in the UserForm
                    // when creating a user for a different organization, and that organization will have different Teams
                    possibleChoices: additionalProps.selectPossibleTeamsForComponent || selectTeamsForComponent,
                    isLimitedToTeamsEnabled: selectIsLimitedToTeamsEnabled,
                    shouldRenderFormSection: selectShouldRenderFormSection,
                    ...selector && {
                        teamUrisArray: selector
                    },
                    ...isLimitedToCreatorSelector && {
                        isLimitedToCreator: isLimitedToCreatorSelector,
                    },
                })
                const _WrapperComponent = connect(
                    wrapperComponentConnection,
                    mapDispatchToProps,
                )(WrapperComponent)
                this._WrapperComponent = _WrapperComponent
            }
        }

        render = () => (
            <LimitedToTeamsSection
                props={this.props}
                additionalProps={additionalProps}
                shouldHideTeamPermissioningFormComponent={
                    shouldHideTeamPermissioningFormComponent
                    || getUserdataField("should_hide_team_permissionining_in_forms")
                } // not worth connecting for this
                _WrapperComponent={this._WrapperComponent}
                _TeamSelectSection={this._TeamSelectSection}
            />
        )
    }

    const connectedWrappedTeamSelectSectionConnection = createStructuredSelector({
        loadingTeams: selectIsTeamsLoading,
        shouldHideTeamPermissioningFormComponent: (
            shouldHideTeamPermissioningFormComponentSelector
            || selectShouldHideTeamPermissioningInForms
        ),
    })

    return connect(connectedWrappedTeamSelectSectionConnection)(ConnectedWrappedTeamSelectSection)
}

export default createConnectedTeamSelectSection
